import React, { useState, useCallback, useEffect } from 'react';
import { 
  Card, 
  DatePicker, 
  Slider, 
  Space, 
  Typography, 
  Button, 
  Divider,
  Tooltip,
  Switch,
  InputNumber,
  Alert,
  Badge,
  Dropdown
} from 'antd';
import { 
  SettingOutlined, 
  CalendarOutlined, 
  BarChartOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import './index.css';
import { analysisApi } from '../../services';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 策略权重接口
interface StrategyWeights {
  trend_following: number;
  mean_reversion: number;
  statistical_arbitrage: number;
  volatility_trading: number;
  market_sentiment: number;
  ml_forecasting: number;
  money_flow: number;
  risk_parity: number;
  sector_rotation: number;
  fundamental_quant: number;
  enhanced_event: number;
  event_driven: number;
  grid_trading: number;
  factor_investing: number;
}

// 分析参数接口
interface AnalysisParameters {
  dateRange: [Dayjs, Dayjs] | null;
  strategyWeights: StrategyWeights;
  selectedStrategies: (keyof StrategyWeights)[];
  autoUpdate: boolean;
  updateInterval: number; // 秒
  pairedSymbol?: string;
  // 统计套利参数
  stat_arb_z_entry?: number;
  stat_arb_z_exit?: number;
  stat_arb_lookback?: number;
}

interface ParameterSettingsProps {
  onParametersChange: (parameters: AnalysisParameters) => void;
  disabled?: boolean;
  loading?: boolean;
  onConfigChange?: (config: any) => void; // 兼容旧接口
}

// 默认策略权重（均衡分配）
const DEFAULT_WEIGHTS: StrategyWeights = {
  trend_following: 7.1,
  mean_reversion: 7.1,
  statistical_arbitrage: 7.1,
  volatility_trading: 7.1,
  market_sentiment: 7.1,
  ml_forecasting: 7.1,
  money_flow: 7.1,
  risk_parity: 7.1,
  sector_rotation: 7.1,
  fundamental_quant: 7.1,
  enhanced_event: 7.1,
  event_driven: 7.1,
  grid_trading: 7.1,
  factor_investing: 7.1
};

// 针对A股优化的推荐权重
const RECOMMENDED_WEIGHTS: StrategyWeights = {
  trend_following: 12.0,      // 趋势策略在A股表现稳定
  mean_reversion: 10.0,      // 均值回归适合A股震荡特性
  volatility_trading: 9.0,    // A股波动率高，该策略有效
  market_sentiment: 8.0,      // 情绪驱动是A股重要特征
  ml_forecasting: 8.0,        // 机器学习模型有优势
  risk_parity: 8.0,           // 风险控制很重要
  sector_rotation: 8.0,       // 板块轮动是A股特色
  event_driven: 7.0,          // 事件驱动收益可观
  grid_trading: 7.0,          // 网格交易适合震荡市
  statistical_arbitrage: 5.0, // 需要数据支持，权重降低
  money_flow: 5.0,            // 需要真实资金流数据
  fundamental_quant: 5.0,      // 需要财务数据支持
  enhanced_event: 4.0,        // 事件识别需要优化
  factor_investing: 4.0        // 需要多因子数据
};

// 平衡型（14策略全覆盖，总和100）
const BALANCED_WEIGHTS: StrategyWeights = {
  trend_following: 16.0,
  mean_reversion: 11.0,
  market_sentiment: 8.0,
  ml_forecasting: 0.0,
  statistical_arbitrage: 10.0,
  volatility_trading: 10.0,
  fundamental_quant: 5.0,
  risk_parity: 10.0,
  sector_rotation: 8.0,
  factor_investing: 5.0,
  grid_trading: 2.0,
  event_driven: 8.0,
  money_flow: 4.0,
  enhanced_event: 3.0
};

// 牛市进取（倾向方向性，总和100）
const BULLISH_WEIGHTS: StrategyWeights = {
  trend_following: 20.0,
  mean_reversion: 12.0,
  market_sentiment: 8.0,
  ml_forecasting: 5.0,
  statistical_arbitrage: 8.0,
  volatility_trading: 7.0,
  fundamental_quant: 5.0,
  risk_parity: 4.0,
  sector_rotation: 10.0,
  factor_investing: 6.0,
  grid_trading: 0.0,
  event_driven: 8.0,
  money_flow: 4.0,
  enhanced_event: 3.0
};

// 熊市防守（倾向中性与配置，总和100）
const DEFENSIVE_WEIGHTS: StrategyWeights = {
  trend_following: 10.0,
  mean_reversion: 5.0,
  market_sentiment: 3.0,
  ml_forecasting: 2.0,
  statistical_arbitrage: 15.0,
  volatility_trading: 12.0,
  fundamental_quant: 8.0,
  risk_parity: 15.0,
  sector_rotation: 5.0,
  factor_investing: 8.0,
  grid_trading: 2.0,
  event_driven: 8.0,
  money_flow: 4.0,
  enhanced_event: 3.0
};

// 稳健中性（提高市场中性组合占比与敏感度，总和100）
const NEUTRAL_ENHANCED_WEIGHTS: StrategyWeights = {
  trend_following: 10.0,
  mean_reversion: 8.0,
  market_sentiment: 6.0,
  ml_forecasting: 4.0,
  statistical_arbitrage: 12.0,
  volatility_trading: 12.0,
  fundamental_quant: 10.0,
  risk_parity: 12.0,
  sector_rotation: 6.0,
  factor_investing: 6.0,
  grid_trading: 2.0,
  event_driven: 6.0,
  money_flow: 4.0,
  enhanced_event: 2.0
};

// 策略信息
const STRATEGY_INFO: Record<keyof StrategyWeights, { name: string; description: string; color: string }> = {
  trend_following: {
    name: '趋势跟踪',
    description: '基于价格趋势和技术指标的策略',
    color: '#1890ff'
  },
  mean_reversion: {
    name: '均值回归',
    description: '基于价格偏离均值的回归策略',
    color: '#52c41a'
  },
  statistical_arbitrage: {
    name: '统计套利',
    description: '基于统计关系的套利策略',
    color: '#fa8c16'
  },
  volatility_trading: {
    name: '波动率交易',
    description: '基于波动率扩张/收缩的交易信号',
    color: '#c41d7f'
  },
  market_sentiment: {
    name: '市场情绪',
    description: '动量/RSI/量能合成情绪指数',
    color: '#722ed1'
  },
  ml_forecasting: {
    name: '机器学习预测',
    description: '已升级为LSTM/GRU时序模型，可在权重中参与加权',
    color: '#13c2c2'
  },
  money_flow: {
    name: '资金流向',
    description: '已接入：FinanceMCP主力净流指标（净额/占比），量价代理为回退',
    color: '#eb2f96'
  },
  risk_parity: {
    name: '风险平价',
    description: '逆波动率仓位建议（单资产简化版）',
    color: '#531dab'
  },
  sector_rotation: {
    name: '板块轮动',
    description: '动量与价位相对均线位置的轮动信号',
    color: '#08979c'
  },
  fundamental_quant: {
    name: '基本面量化',
    description: '稳定性与回撤构造质量分数（代理）',
    color: '#597ef7'
  },
  enhanced_event: {
    name: '事件驱动增强',
    description: '收益与量能脉冲识别事件窗口',
    color: '#a0d911'
  },
  event_driven: {
    name: '事件驱动',
    description: '价格变化与放量识别事件窗口（简化版）',
    color: '#bfbfbf'
  },
  grid_trading: {
    name: '网格交易',
    description: '基于均线与间距的上下轨网格信号',
    color: '#ff85c0'
  },
  factor_investing: {
    name: '因子投资',
    description: '已接入：FinanceMCP财务指标（PE/PB/ROE；缺失时回退稳定/回撤代理）',
    color: '#ffd666'
  }
};

const ParameterSettings: React.FC<ParameterSettingsProps> = ({
  onParametersChange,
  disabled = false,
  loading = false,
  onConfigChange
}) => {
  // 状态管理
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(1, 'year'),
    dayjs()
  ]);
  const [strategyWeights, setStrategyWeights] = useState<StrategyWeights>(DEFAULT_WEIGHTS);
  const [loadedStrategies, setLoadedStrategies] = useState<(keyof StrategyWeights)[]>(Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]);
  const [enabledMap, setEnabledMap] = useState<Record<keyof StrategyWeights, boolean>>(() => {
    const init: Record<keyof StrategyWeights, boolean> = {} as any;
    (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).forEach((k) => init[k] = true);
    return init;
  });
  const [autoUpdate, setAutoUpdate] = useState<boolean>(false);
  const [updateInterval, setUpdateInterval] = useState<number>(30);
  const [weightsValid, setWeightsValid] = useState<boolean>(true);

  // 验证权重总和
  const validateWeights = useCallback((weights: StrategyWeights): boolean => {
    const total = loadedStrategies.filter((k) => enabledMap[k]).reduce((sum, key) => sum + (weights[key] || 0), 0);
    return Math.abs(total - 100) < 0.1;
  }, [loadedStrategies, enabledMap]);

  // 权重变化处理
  const handleWeightChange = useCallback((strategy: keyof StrategyWeights, value: number) => {
    const newWeights = { ...strategyWeights, [strategy]: value };
    setStrategyWeights(newWeights);
    
    const isValid = validateWeights(newWeights);
    setWeightsValid(isValid);
    
    // 如果权重有效，触发参数变化
    if (isValid) {
      const parameters: AnalysisParameters = {
        dateRange,
        strategyWeights: newWeights,
        selectedStrategies: loadedStrategies.filter((k) => enabledMap[k]),
        autoUpdate,
        updateInterval
      };
      onParametersChange(parameters);
    }
  }, [strategyWeights, dateRange, autoUpdate, updateInterval, validateWeights, onParametersChange, loadedStrategies, enabledMap]);

  // 日期范围变化处理
  const handleDateRangeChange = useCallback((dates: [Dayjs | null, Dayjs | null] | null) => {
    // 确保dates是有效的日期范围
    const validDates = dates && dates[0] && dates[1] ? [dates[0], dates[1]] as [Dayjs, Dayjs] : null;
    setDateRange(validDates);
    
    if (weightsValid) {
      const parameters: AnalysisParameters = {
        dateRange: validDates,
        strategyWeights,
        selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
        autoUpdate,
        updateInterval
      };
      onParametersChange(parameters);
    }
  }, [strategyWeights, autoUpdate, updateInterval, weightsValid, onParametersChange, loadedStrategies, enabledMap]);

  // 自动更新设置变化
  const handleAutoUpdateChange = useCallback((checked: boolean) => {
    setAutoUpdate(checked);
    
    if (weightsValid) {
      const parameters: AnalysisParameters = {
        dateRange,
        strategyWeights,
        selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
        autoUpdate: checked,
        updateInterval
      };
      onParametersChange(parameters);
    }
  }, [dateRange, strategyWeights, updateInterval, weightsValid, onParametersChange, loadedStrategies, enabledMap]);

  // 更新间隔变化
  const handleIntervalChange = useCallback((value: number | null) => {
    const interval = value || 30;
    setUpdateInterval(interval);
    
    if (weightsValid) {
      const parameters: AnalysisParameters = {
        dateRange,
        strategyWeights,
        selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
        autoUpdate,
        updateInterval: interval
      };
      onParametersChange(parameters);
    }
  }, [dateRange, strategyWeights, autoUpdate, weightsValid, onParametersChange, loadedStrategies, enabledMap]);

  // 重置权重
  const handleResetWeights = useCallback(() => {
    setStrategyWeights(DEFAULT_WEIGHTS);
    setWeightsValid(true);
    
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: DEFAULT_WEIGHTS,
      selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [dateRange, autoUpdate, updateInterval, onParametersChange, loadedStrategies, enabledMap]);

  // 应用推荐权重（针对A股优化）
  const handleApplyRecommendedWeights = useCallback(() => {
    setStrategyWeights(RECOMMENDED_WEIGHTS);
    setWeightsValid(true);
    
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: RECOMMENDED_WEIGHTS,
      selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [dateRange, autoUpdate, updateInterval, onParametersChange, loadedStrategies, enabledMap]);

  // 将当前权重提交到后端（0-1 归一化）
  const persistWeights = useCallback(async (weights: StrategyWeights) => {
    const enabledKeys = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]);
    const total = enabledKeys.reduce((sum, k) => sum + (weights[k] || 0), 0);
    if (total <= 0) return;
    const weights01: Record<string, number> = {};
    (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).forEach((k) => {
      const v = enabledMap[k] ? (weights[k] || 0) : 0;
      weights01[k] = Math.round((v / 100) * 10000) / 10000;
    });
    await analysisApi.updateStrategyWeights(weights01);
  }, [enabledMap]);

  const applyTemplate = useCallback((tpl: StrategyWeights) => {
    setStrategyWeights(tpl);
    setWeightsValid(true);
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: tpl,
      selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
    persistWeights(tpl);
  }, [dateRange, enabledMap, autoUpdate, updateInterval, onParametersChange, persistWeights]);

  const applyNeutralEnhanced = useCallback(() => {
    // 设置权重
    applyTemplate(NEUTRAL_ENHANCED_WEIGHTS);
    // 同步组聚合与敏感度（通过本地存储传递到分析API）
    try {
      localStorage.setItem('USE_GROUP_RISK_COMPOSITE', '1');
      localStorage.setItem('FINAL_CONF_FROM_GROUPS', '1');
      localStorage.setItem('FINAL_RISK_FROM_GROUPS', '1');
      localStorage.setItem('MARKET_NEUTRAL_SENSITIVITY', '1.5');
    } catch { /* ignore */ }
  }, [applyTemplate]);

  // 自动调整权重使总和为100
  const handleAutoAdjustWeights = useCallback(() => {
    const enabledKeys = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]);
    const total = enabledKeys.reduce((sum, key) => sum + (strategyWeights[key] || 0), 0);
    if (total === 0) return;
    
    const adjustedWeights: StrategyWeights = {} as StrategyWeights;
    // 仅对启用的策略进行均衡，禁用的置为0
    (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).forEach((key) => {
      if (enabledMap[key]) {
        const value = strategyWeights[key] || 0;
        adjustedWeights[key] = Math.round((value / total) * 1000) / 10; // 保留一位小数
      } else {
        adjustedWeights[key] = 0;
      }
    });
    
    setStrategyWeights(adjustedWeights);
    setWeightsValid(true);
    
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: adjustedWeights,
      selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [strategyWeights, dateRange, autoUpdate, updateInterval, onParametersChange, loadedStrategies, enabledMap]);

  // 组内等权 + 全局归一化
  const handleGroupEqualAndNormalize = useCallback((groupKeys: (keyof StrategyWeights)[]) => {
    // 先组内等权
    const enabledKeysInGroup = groupKeys.filter(k => enabledMap[k]);
    if (enabledKeysInGroup.length > 0) {
      const groupTotal = enabledKeysInGroup.reduce((s,k)=> s + (strategyWeights[k] || 0), 0);
      const equal = Math.round((groupTotal / enabledKeysInGroup.length) * 10) / 10;
      const newWeights: StrategyWeights = { ...strategyWeights };
      enabledKeysInGroup.forEach(k => newWeights[k] = equal);
      setStrategyWeights(newWeights);
      const parameters: AnalysisParameters = {
        dateRange,
        strategyWeights: newWeights,
        selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => enabledMap[k]),
        autoUpdate,
        updateInterval
      };
      onParametersChange(parameters);
    }
    // 再全局归一化到100%
    handleAutoAdjustWeights();
  }, [strategyWeights, enabledMap, dateRange, autoUpdate, updateInterval, onParametersChange, loadedStrategies, handleAutoAdjustWeights]);

  // 初始化参数
  useEffect(() => {
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights,
      selectedStrategies: loadedStrategies.filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, []); // 只在组件挂载时执行

  // 动态拉取后端策略状态，更新可用策略列表
  useEffect(() => {
    (async () => {
      const onceRef = (window as any).__fetch_strategy_status_once__ || false;
      if (onceRef) return;
      (window as any).__fetch_strategy_status_once__ = true;
      try {
        const status = await analysisApi.getStrategyStatus();
        const strategiesObj = status?.strategies || {};
        // 选择后端已启用/ready的策略，并且我们在前端有对应中文名称映射
        const enabledKeys = Object.keys(strategiesObj)
          .filter((k) => strategiesObj[k]?.enabled !== false && strategiesObj[k]?.ready !== false)
          .filter((k) => k in STRATEGY_INFO) as (keyof StrategyWeights)[];
        if (enabledKeys.length > 0) {
          setLoadedStrategies(enabledKeys);
          // 若后端返回了权重，尝试覆盖默认权重并归一到百分比
          const newWeights = { ...strategyWeights };
          enabledKeys.forEach((k) => {
            const w = strategiesObj[k]?.weight;
            if (typeof w === 'number' && w >= 0) {
              newWeights[k] = Math.round(w * 1000) / 10; // 0-1 → 百分比（保留1位）
            }
          });
          setStrategyWeights(newWeights);
          // 根据后端enabled初始化本地开关
          const newEnabledMap: Record<keyof StrategyWeights, boolean> = { ...enabledMap } as any;
          (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).forEach((k) => {
            newEnabledMap[k] = enabledKeys.includes(k);
          });
          setEnabledMap(newEnabledMap);
        }
      } catch (err) {
        // 拉取失败不影响使用，保留本地默认列表
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 计算权重总和
  const totalWeight = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[])
    .filter((k) => enabledMap[k])
    .reduce((sum, key) => sum + (strategyWeights[key] || 0), 0);

  // 组别映射 + 折叠状态
  const GROUPS: Record<string, (keyof StrategyWeights)[]> = {
    方向性策略: ['trend_following','mean_reversion','ml_forecasting','market_sentiment'],
    市场中性策略: ['statistical_arbitrage','volatility_trading','fundamental_quant'],
    资产配置策略: ['grid_trading','factor_investing','risk_parity','sector_rotation'],
    事件驱动策略: ['event_driven','money_flow','enhanced_event']
  };
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleCollapse = (g: string) => setCollapsed(prev => ({ ...prev, [g]: !prev[g] }));

  // 组操作：组内等权、禁用组、启用组
  const handleGroupEqual = useCallback((groupKeys: (keyof StrategyWeights)[]) => {
    const enabledKeys = groupKeys.filter(k => enabledMap[k]);
    if (enabledKeys.length === 0) return;
    const groupTotal = enabledKeys.reduce((s,k)=> s + (strategyWeights[k] || 0), 0);
    const equal = Math.round((groupTotal / enabledKeys.length) * 10) / 10;
    const newWeights: StrategyWeights = { ...strategyWeights };
    enabledKeys.forEach(k => newWeights[k] = equal);
    setStrategyWeights(newWeights);
    // 通知变更
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: newWeights,
      selectedStrategies: loadedStrategies.filter((k) => enabledMap[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [strategyWeights, enabledMap, dateRange, autoUpdate, updateInterval, onParametersChange, loadedStrategies]);

  const handleGroupDisable = useCallback((groupKeys: (keyof StrategyWeights)[]) => {
    const newEnabled = { ...enabledMap };
    const newWeights: StrategyWeights = { ...strategyWeights };
    groupKeys.forEach(k => { newEnabled[k] = false; newWeights[k] = 0; });
    setEnabledMap(newEnabled);
    setStrategyWeights(newWeights);
    // 全局归一化到100%
    const enabledKeys = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter(k => newEnabled[k]);
    const total = enabledKeys.reduce((s,k)=> s + (newWeights[k] || 0), 0);
    if (total > 0) {
      enabledKeys.forEach(k => newWeights[k] = Math.round((newWeights[k] / total) * 1000) / 10);
      setStrategyWeights(newWeights);
    }
    setWeightsValid(true);
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: newWeights,
      selectedStrategies: enabledKeys,
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [enabledMap, strategyWeights, loadedStrategies, dateRange, autoUpdate, updateInterval, onParametersChange]);

  const handleGroupEnable = useCallback((groupKeys: (keyof StrategyWeights)[]) => {
    const newEnabled = { ...enabledMap };
    const newWeights: StrategyWeights = { ...strategyWeights };
    const currentlyEnabled = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter(k => newEnabled[k]);
    const numEnabled = currentlyEnabled.length + groupKeys.filter(k => !newEnabled[k]).length;
    groupKeys.forEach(k => { newEnabled[k] = true; });
    // 将新启用策略均分一定权重（保持总和为100%）
    const equal = Math.round((100 / numEnabled) * 10) / 10;
    (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).forEach(k => { if (newEnabled[k]) newWeights[k] = equal; });
    setEnabledMap(newEnabled);
    setStrategyWeights(newWeights);
    setWeightsValid(true);
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: newWeights,
      selectedStrategies: (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter(k => newEnabled[k]),
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [enabledMap, strategyWeights, loadedStrategies, dateRange, autoUpdate, updateInterval, onParametersChange]);

  // 切换策略启用/禁用，并自动均衡启用策略的权重
  const handleToggleStrategy = useCallback((key: keyof StrategyWeights, enabled: boolean) => {
    const newEnabled = { ...enabledMap, [key]: enabled };
    setEnabledMap(newEnabled);
    const newWeights = { ...strategyWeights };
    if (!enabled) {
      newWeights[key] = 0;
    }
    const enabledKeys = (Object.keys(STRATEGY_INFO) as (keyof StrategyWeights)[]).filter((k) => newEnabled[k]);
    const currentTotal = enabledKeys.reduce((sum, k) => sum + (newWeights[k] || 0), 0);
    let targetTotal = 100;
    if (currentTotal === 0 && enabledKeys.length > 0) {
      const equal = Math.round((100 / enabledKeys.length) * 10) / 10;
      enabledKeys.forEach((k) => newWeights[k] = equal);
    } else if (currentTotal !== 100 && enabledKeys.length > 0) {
      enabledKeys.forEach((k) => {
        newWeights[k] = Math.round((newWeights[k] / currentTotal * targetTotal) * 10) / 10;
      });
    }
    setStrategyWeights(newWeights);
    setWeightsValid(true);
    const parameters: AnalysisParameters = {
      dateRange,
      strategyWeights: newWeights,
      selectedStrategies: enabledKeys,
      autoUpdate,
      updateInterval
    };
    onParametersChange(parameters);
  }, [enabledMap, strategyWeights, loadedStrategies, dateRange, autoUpdate, updateInterval, onParametersChange]);

  return (
    <div className="parameter-settings">
      <Card 
        title={
          <Space>
            <SettingOutlined />
            <Title level={4} style={{ margin: 0 }}>参数设置</Title>
          </Space>
        }
        size="small"
        loading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          
          {/* 时间范围设置 */}
          <div className="setting-section">
            <div className="setting-header">
              <CalendarOutlined />
              <Text strong>分析时间范围</Text>
            </div>
            <RangePicker
              id="date-range-picker"
              value={dateRange}
              onChange={handleDateRangeChange}
              disabled={disabled}
              style={{ width: '100%' }}
              placeholder={['开始日期', '结束日期']}
              allowClear={false}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              presets={[
                { label: '最近一月', value: [dayjs().subtract(1, 'month'), dayjs()] },
                { label: '最近三月', value: [dayjs().subtract(3, 'month'), dayjs()] },
                { label: '最近半年', value: [dayjs().subtract(6, 'month'), dayjs()] },
                { label: '最近一年', value: [dayjs().subtract(1, 'year'), dayjs()] },
                { label: '最近三年', value: [dayjs().subtract(3, 'year'), dayjs()] },
                { label: '今年以来', value: [dayjs().startOf('year'), dayjs()] },
              ]}
            />
          </div>

        <Divider />

          {/* A股交易约束设置 */}
          <div className="setting-section a-share-constraints">
            <div className="setting-header">
              <InfoCircleOutlined />
              <Text strong>A股交易约束</Text>
              <Tooltip title="设置涨跌停、滑点和成交率，用于风险评估与信号约束">
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>
            <div className="constraint-grid">
              <div className="constraint-item">
                <Text type="secondary" className="constraint-label">涨停幅度(%)</Text>
                <InputNumber 
                  min={0} 
                  max={100} 
                  step={0.1} 
                  size="small"
                  className="constraint-input"
                  defaultValue={Number(localStorage.getItem('PRICE_LIMIT_UP') || 10)}
                  onChange={(v)=>{ localStorage.setItem('PRICE_LIMIT_UP', String(v ?? 10)); }}
                />
              </div>
              <div className="constraint-item">
                <Text type="secondary" className="constraint-label">跌停幅度(%)</Text>
                <InputNumber 
                  min={0} 
                  max={100} 
                  step={0.1} 
                  size="small"
                  className="constraint-input"
                  defaultValue={Number(localStorage.getItem('PRICE_LIMIT_DOWN') || 10)}
                  onChange={(v)=>{ localStorage.setItem('PRICE_LIMIT_DOWN', String(v ?? 10)); }}
                />
              </div>
              <div className="constraint-item">
                <Text type="secondary" className="constraint-label">滑点(bps)</Text>
                <InputNumber 
                  min={0} 
                  max={100} 
                  step={1} 
                  size="small"
                  className="constraint-input"
                  defaultValue={Number(localStorage.getItem('SLIPPAGE_BPS') || 10)}
                  onChange={(v)=>{ localStorage.setItem('SLIPPAGE_BPS', String(v ?? 10)); }}
                />
              </div>
              <div className="constraint-item">
                <Text type="secondary" className="constraint-label">成交率(0-1)</Text>
                <InputNumber 
                  min={0} 
                  max={1} 
                  step={0.05} 
                  size="small"
                  className="constraint-input"
                  defaultValue={Number(localStorage.getItem('FILL_RATE') || 0.8)}
                  onChange={(v)=>{ localStorage.setItem('FILL_RATE', String(v ?? 0.8)); }}
                />
              </div>
            </div>
          </div>

          {/* 策略权重设置 */}
          <div className="setting-section">
            <div className="setting-header">
              <BarChartOutlined />
              <Text strong>策略权重配置</Text>
              <Tooltip title="调整各策略在综合分析中的权重，总和应为100%">
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>

            {/* 权重验证提示 */}
            {!weightsValid && (
              <Alert
                message={`权重总和为 ${totalWeight.toFixed(1)}%，应为 100%`}
                type="warning"
                showIcon
                action={
                  <Button size="small" type="link" onClick={handleAutoAdjustWeights}>
                    自动调整
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 分组权重滑块：折叠卡片 + 徽章 + 批量分配 */}
            <div className="strategy-weights">
              {Object.entries(GROUPS).map(([groupLabel, keys]) => {
                const enabledCount = keys.filter(k => enabledMap[k]).length;
                const isCollapsed = collapsed[groupLabel];
                return (
                  <Card
                    key={groupLabel}
                    size="small"
                    title={
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Badge count={enabledCount} showZero color="#1890ff" />
                        <Text strong>{groupLabel}</Text>
                        <Button type="text" size="small" onClick={() => toggleCollapse(groupLabel)}>
                          {isCollapsed ? <DownOutlined /> : <UpOutlined />}
                        </Button>
                      </Space>
                    }
                    style={{ marginBottom: 12 }}
                  >
                    {/* 批量分配操作 */}
                    <div style={{ marginBottom: 8 }}>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            { key: 'equal', label: '组内等权' },
                            { key: 'disable', label: '禁用本组' },
                            { key: 'enable', label: '启用本组' },
                            { key: 'equal_norm', label: '等权归一' }
                          ],
                          onClick: ({ key }) => {
                            if (key === 'equal') handleGroupEqual(keys);
                            if (key === 'disable') handleGroupDisable(keys);
                            if (key === 'enable') handleGroupEnable(keys);
                            if (key === 'equal_norm') handleGroupEqualAndNormalize(keys);
                          }
                        }}
                      >
                        <Button size="small">批量分配</Button>
                      </Dropdown>
                    </div>
                    {/* 策略列表：折叠控制 */}
                    {!isCollapsed && (
                      <div className="strategy-list">
                        {keys.map((key) => {
                          const info = STRATEGY_INFO[key];
                          return (
                            <div key={key} className="strategy-row" style={{ marginBottom: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className="strategy-color" style={{ backgroundColor: info.color, width: 12, height: 12, borderRadius: 2 }} />
                                <Tooltip title={info.description}>
                                  <Text className="strategy-name" style={{ flex: 1, fontSize: 13 }}>{info.name}</Text>
                                </Tooltip>
                                <Switch size="small" checked={enabledMap[key]} onChange={(checked) => handleToggleStrategy(key, checked)} />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                <Slider
                                  min={0}
                                  max={100}
                                  step={0.1}
                                  value={strategyWeights[key]}
                                  onChange={(value) => handleWeightChange(key, value)}
                                  disabled={disabled || !enabledMap[key]}
                                  tooltip={{ formatter: (v) => `${v}%` }}
                                  style={{ flex: 1 }}
                                />
                                <InputNumber
                                  id={`weight-input-${key}`}
                                  min={0}
                                  max={100}
                                  step={0.1}
                                  value={strategyWeights[key]}
                                  onChange={(v) => handleWeightChange(key, v || 0)}
                                  disabled={disabled || !enabledMap[key]}
                                  size="small"
                                  formatter={(v) => `${v}%`}
                                  parser={(v) => parseFloat(v?.replace('%', '') || '0')}
                                  style={{ width: 80 }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* 权重操作按钮 */}
            <div className="weight-actions">
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  总计: {totalWeight.toFixed(1)}%
                </Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    icon={<ReloadOutlined />} 
                    onClick={handleResetWeights}
                    disabled={disabled}
                    style={{ fontSize: '11px', padding: '0 6px', height: '22px', flexShrink: 0 }}
                  >
                    重置
                  </Button>
                  <Button 
                    size="small" 
                    onClick={handleAutoAdjustWeights}
                    style={{ fontSize: '11px', padding: '0 6px', height: '22px', flexShrink: 0 }}
                  >
                    归一化
                  </Button>
                  <Button 
                    size="small" 
                    type="primary"
                    onClick={handleApplyRecommendedWeights}
                    disabled={disabled}
                    style={{ fontSize: '11px', padding: '0 6px', height: '22px', flexShrink: 0 }}
                  >
                    推荐
                  </Button>
                </div>
              </div>
            </div>
          {/* 预设模板按钮（平衡/进取/防守） */}
          <div className="weight-actions" style={{ marginTop: 6 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', width: '100%' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => applyTemplate(BALANCED_WEIGHTS)} style={{ fontSize: '11px', padding: '0 6px', height: '22px' }}>平衡型</Button>
                <Button size="small" onClick={() => applyTemplate(BULLISH_WEIGHTS)} style={{ fontSize: '11px', padding: '0 6px', height: '22px' }}>牛市进取</Button>
                <Button size="small" onClick={() => applyTemplate(DEFENSIVE_WEIGHTS)} style={{ fontSize: '11px', padding: '0 6px', height: '22px' }}>熊市防守</Button>
                <Button size="small" onClick={applyNeutralEnhanced} style={{ fontSize: '11px', padding: '0 6px', height: '22px' }}>稳健中性</Button>
              </div>
            </div>
          </div>

          {/* 统计套利参数（Z-Score阈值与回看周期） - 已移除，使用后端默认值 (2.0/0.5/60) */}
          </div>

          <Divider />

          {/* 自动更新设置 */}
          <div className="setting-section">
            <div className="setting-header">
              <Text strong>自动更新</Text>
            </div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div className="auto-update-switch">
                <Switch
                  id="auto-update-switch"
                  checked={autoUpdate}
                  onChange={handleAutoUpdateChange}
                  disabled={disabled}
                />
                <Text style={{ marginLeft: 8 }}>启用实时数据自动更新</Text>
              </div>
              
              {autoUpdate && (
                <div className="update-interval">
                  <Text>更新间隔:</Text>
                  <InputNumber
                    id="update-interval-input"
                    min={10}
                    max={300}
                    value={updateInterval}
                    onChange={handleIntervalChange}
                    disabled={disabled}
                    addonAfter="秒"
                    style={{ marginLeft: 8 }}
                  />
                </div>
              )}
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ParameterSettings;
